import React from 'react'

import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import ButtonBase from '@mui/material/ButtonBase'
import Avatar from '@mui/material/Avatar'
import PhoneOutlined from '@mui/icons-material/PhoneOutlined'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import CheckOutlined from '@mui/icons-material/CheckOutlined'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'
import { keyframes } from '@mui/system'

import { useSearchQuery } from 'src/hooks/useSearchQuery'
import UserIcon from 'src/layouts/components/UserIcon'
import {
  useConnectCall,
  useTwilioNewActions,
  useTwilioNewSelect,
} from 'src/hooks/useTwilio'
import { useNumbersActions, useNumbersSelect } from 'src/hooks/useNumbers'
import { Drivers } from 'src/services/accounts'
import type { CallPhase } from 'src/store/modules/twilio/state'
import CallStatusWave from './CallStatusWave'

type Availability = 'unavailable' | 'noNumber' | 'registering' | 'ready'

/** Call states from the mini-widget reference. Missed calls are intentionally absent. */
type CallWidgetState =
  | 'incoming'
  | 'outgoing'
  | 'connecting'
  | 'ringing'
  | 'active'
  | 'rejected'
  | 'cancelled'
  | 'disconnected'
  | 'error'

type WidgetState = Availability | CallWidgetState

type NoticeKind = 'rejected' | 'cancelled' | 'disconnected' | 'error'

const ARIA_LABEL: Record<WidgetState, string> = {
  unavailable: 'Phone unavailable, device not registered',
  noNumber: 'Phone unavailable, no phone number available',
  registering: 'Registering device',
  ready: 'Phone ready',
  incoming: 'Incoming call',
  outgoing: 'Outgoing call',
  connecting: 'Connecting call',
  ringing: 'Ringing',
  active: 'Call in progress',
  rejected: 'Call rejected',
  cancelled: 'Call cancelled',
  disconnected: 'Call ended',
  error: 'Call failed',
}

const contentIn = keyframes`
  from { transform: translateX(-5px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`

const formatDuration = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0'
  )}`
}

const useCallDuration = (acceptedAt: number | null) => {
  const getSeconds = () =>
    acceptedAt ? Math.max(Math.floor((Date.now() - acceptedAt) / 1000), 0) : 0
  const [seconds, setSeconds] = React.useState(getSeconds)

  React.useEffect(() => {
    setSeconds(getSeconds())
    if (!acceptedAt) return

    const timer = setInterval(() => setSeconds(getSeconds()), 1000)

    return () => clearInterval(timer)
  }, [acceptedAt])

  return seconds
}

const CallerDetails = ({ label, name }: { label: string; name: string }) => (
  <Stack minWidth={0} flex={1} gap="3px">
    <Typography
      noWrap
      fontSize={9}
      fontWeight={740}
      letterSpacing="0.075em"
      lineHeight={1.15}
      textTransform="uppercase"
      color="text.secondary"
    >
      {label}
    </Typography>
    <Typography
      noWrap
      fontSize={13}
      fontWeight={740}
      lineHeight={1.25}
      color="text.primary"
    >
      {name}
    </Typography>
  </Stack>
)

const RegisteringContent = () => (
  <>
    <Stack
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      sx={{
        color: 'success.dark',
        width: 50,
        height: 50,
        borderRadius: '50%',
        backgroundColor: 'var(--app-widget-content-surface)',
      }}
    >
      <CallStatusWave size={20} />
    </Stack>
    <CallerDetails label="Connecting..." name="Registering device" />
  </>
)

const Mark = ({
  tone,
  children,
}: {
  tone: 'mint' | 'pink' | 'neutral'
  children: React.ReactNode
}) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    sx={{
      width: 44,
      height: 44,
      borderRadius: '50%',
      color:
        tone === 'pink'
          ? '#D45459'
          : tone === 'mint'
          ? 'var(--app-active-color)'
          : 'text.secondary',
      backgroundColor:
        tone === 'pink'
          ? 'rgba(212, 84, 89, 0.12)'
          : tone === 'mint'
          ? 'var(--app-active-bg)'
          : 'var(--app-widget-content-surface)',
    }}
  >
    {children}
  </Stack>
)

const RoundAction = ({
  label,
  tone,
  onClick,
  children,
}: {
  label: string
  tone: 'danger' | 'accept'
  onClick: () => void
  children: React.ReactNode
}) => (
  <ButtonBase
    aria-label={label}
    onClick={(event) => {
      event.stopPropagation()
      onClick()
    }}
    sx={{
      width: 38,
      height: 38,
      flexShrink: 0,
      borderRadius: '50%',
      color: 'common.white',
      backgroundColor: tone === 'danger' ? '#D45459' : '#176C4A',
      '&:hover': { filter: 'brightness(0.95)' },
      '&:focus-visible': {
        outline: '2px solid var(--app-active-border)',
        outlineOffset: 2,
      },
    }}
  >
    {children}
  </ButtonBase>
)

const disabledSx = {
  width: 62,
  height: 62,
  color: 'text.disabled',
  backgroundColor: 'var(--app-widget-content-surface)',
  boxShadow: 'none',
  opacity: 0.56,
}

const wideCallSx = {
  width: 280,
  height: 62,
  py: '6px',
  px: '7px',
  color: 'text.primary',
  cursor: 'pointer',
}

const shellVariantSx: Record<WidgetState, object> = {
  unavailable: disabledSx,
  noNumber: disabledSx,
  registering: {
    width: 240,
    height: 62,
    py: '7px',
    pl: '8px',
    pr: '16px',
    color: 'text.secondary',
  },
  ready: {
    width: 62,
    height: 62,
    cursor: 'pointer',
    color: 'var(--app-active-color)',
    borderColor: 'var(--app-active-border)',
    backgroundColor: 'var(--app-active-bg)',
    '&:hover': { backgroundColor: 'var(--app-menu-hover-bg)' },
    '&:focus-visible': {
      outline: '3px solid var(--app-active-border)',
      outlineOffset: 3,
    },
  },
  incoming: wideCallSx,
  outgoing: wideCallSx,
  connecting: wideCallSx,
  ringing: wideCallSx,
  active: wideCallSx,
  rejected: wideCallSx,
  cancelled: wideCallSx,
  disconnected: wideCallSx,
  error: wideCallSx,
}

const LIVE_STATES = new Set<WidgetState>([
  'incoming',
  'outgoing',
  'connecting',
  'ringing',
  'active',
])

const phaseToCallState = (phase: CallPhase): CallWidgetState | null => {
  if (phase === 'incoming') return 'incoming'
  if (phase === 'dialing') return 'outgoing'
  if (phase === 'ringing') return 'ringing'
  if (phase === 'connected') return 'active'
  if (phase === 'reconnecting') return 'connecting'

  return null
}

const CommunicationWidgetLayout = () => {
  const { setQuery } = useSearchQuery()
  const device = useTwilioNewSelect.device()
  const { phase, acceptedAt } = useTwilioNewSelect.call()
  const participants = useTwilioNewSelect.participants()
  const getUserNumbersStatus = useNumbersActions.getNumbers.status()
  const numbers = useNumbersSelect.numbers()
  const accept = useTwilioNewActions.accept()
  const reject = useTwilioNewActions.reject()
  const disconnect = useTwilioNewActions.disconnect()
  const connect = useConnectCall()
  const duration = useCallDuration(
    phase === 'connected' || phase === 'reconnecting' ? acceptedAt : null
  )

  const hasNumber = numbers.data.some((acc) => acc.driver === Drivers.TWILIO)
  const party = participants[0]
  const partyName = party?.name || party?.number || 'Unknown caller'
  const partyRef = React.useRef(partyName)
  const numberRef = React.useRef(party?.number)
  const durationRef = React.useRef<string | null>(null)
  const phaseRef = React.useRef(phase)

  if (phase !== 'idle') {
    partyRef.current = partyName
    numberRef.current = party?.number
    if (acceptedAt) durationRef.current = formatDuration(duration)
  }

  const [notice, setNotice] = React.useState<{
    kind: NoticeKind
    name: string
    duration: string | null
  } | null>(null)

  React.useEffect(() => {
    const previous = phaseRef.current
    phaseRef.current = phase

    if (phase !== 'idle') {
      setNotice(null)

      return
    }

    if (previous === 'idle') return

    const name = partyRef.current
    const ended = durationRef.current

    if (device.status === 'error') {
      setNotice({ kind: 'error', name, duration: null })
    } else if (previous === 'connected' || previous === 'reconnecting') {
      setNotice({ kind: 'disconnected', name, duration: ended })
    } else if (previous === 'incoming') {
      setNotice({ kind: 'rejected', name, duration: null })
    } else {
      setNotice({ kind: 'cancelled', name, duration: null })
    }
  }, [phase, device.status])

  React.useEffect(() => {
    if (!notice || notice.kind === 'error') return

    const timer = window.setTimeout(() => setNotice(null), 2000)

    return () => window.clearTimeout(timer)
  }, [notice])

  const availability: Availability =
    device.status === 'registering' || getUserNumbersStatus.isPending
      ? 'registering'
      : device.status !== 'registered'
      ? 'unavailable'
      : !hasNumber
      ? 'noNumber'
      : 'ready'

  const state: WidgetState =
    phaseToCallState(phase) ?? notice?.kind ?? availability

  const openPhone = () => setQuery({ modal: 'phone' })
  const retryCall = () => {
    const line = numbers.data.find((acc) => acc.driver === Drivers.TWILIO)
    const toWhom = numberRef.current

    setNotice(null)
    if (line && toWhom) {
      connect({ userId: String(line.id), toWhom })
    } else {
      openPhone()
    }
  }

  const isWide =
    state !== 'unavailable' && state !== 'noNumber' && state !== 'ready'
  const opensPhone = state === 'ready' || LIVE_STATES.has(state)
  const shownName = notice?.name || partyName
  const shownDuration =
    state === 'active'
      ? formatDuration(duration)
      : notice?.duration || durationRef.current || '00:00'

  return (
    <Tooltip
      title={state === 'noNumber' ? 'No phone number available' : ''}
      placement="right"
    >
      <Box
        role={opensPhone ? 'button' : 'status'}
        tabIndex={opensPhone ? 0 : undefined}
        aria-label={ARIA_LABEL[state]}
        aria-disabled={state === 'unavailable' || state === 'noNumber'}
        onClick={opensPhone ? openPhone : undefined}
        onKeyDown={(event) => {
          if (opensPhone && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            openPhone()
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isWide ? 'flex-start' : 'center',
          flexShrink: 0,
          overflow: 'hidden',
          border: '1px solid var(--app-glass-border)',
          borderRadius: 'var(--app-widget-radius)',
          backgroundColor: 'var(--app-widget-surface)',
          boxShadow: 'var(--app-widget-shadow)',
          transition:
            'width 280ms cubic-bezier(0.22, 1, 0.36, 1), height 280ms cubic-bezier(0.22, 1, 0.36, 1), border-color 180ms ease, background-color 180ms ease, color 180ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          ...shellVariantSx[state],
        }}
      >
        <Stack
          key={state}
          direction="row"
          alignItems="center"
          gap={state === 'registering' ? '11px' : '10px'}
          minWidth={0}
          width={isWide ? '100%' : 'auto'}
          sx={{
            animation: `${contentIn} 220ms ease both`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          {state === 'registering' ? (
            <RegisteringContent />
          ) : state === 'incoming' ? (
            <IncomingContent
              name={shownName}
              avatarUrl={party?.avatarUrl}
              onDecline={() => reject()}
              onAccept={() => accept()}
            />
          ) : state === 'outgoing' ||
            state === 'connecting' ||
            state === 'ringing' ? (
            <ProgressContent
              label={
                state === 'outgoing'
                  ? 'Calling...'
                  : state === 'ringing'
                  ? 'Ringing...'
                  : 'Connecting'
              }
              name={shownName}
              onEnd={() => disconnect()}
            />
          ) : state === 'active' ? (
            <ActiveContent
              name={shownName}
              avatarUrl={party?.avatarUrl}
              duration={shownDuration}
              onEnd={() => disconnect()}
            />
          ) : state === 'rejected' || state === 'cancelled' ? (
            <TerminalContent
              tone={state === 'rejected' ? 'pink' : 'neutral'}
              label={state === 'rejected' ? 'Call rejected' : 'Call cancelled'}
              name={shownName}
              icon={<CloseOutlined sx={{ fontSize: 18 }} />}
            />
          ) : state === 'disconnected' ? (
            <TerminalContent
              tone="mint"
              label="Call ended"
              name={shownDuration}
              icon={<CheckOutlined sx={{ fontSize: 18 }} />}
            />
          ) : state === 'error' ? (
            <FailedContent name={shownName} onRetry={retryCall} />
          ) : (
            <UserIcon icon={'andrsrxn:phone'} />
          )}
        </Stack>
      </Box>
    </Tooltip>
  )
}

const IncomingContent = ({
  name,
  onDecline,
  onAccept,
  avatarUrl,
}: {
  name: string
  onDecline: () => void
  onAccept: () => void
  avatarUrl?: string
}) => (
  <>
    <Avatar
      src={avatarUrl}
      alt={name}
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        border: '1px solid',
        borderColor: 'var(--app-avatar-border, rgba(36, 50, 61, 0.1))',
        color: 'var(--app-avatar-color, #32423E)',
        bgcolor: 'var(--app-avatar-bg, rgba(235, 241, 238, 0.94))',
      }}
    >
      <PhoneOutlined sx={{ fontSize: 18 }} />
    </Avatar>
    <CallerDetails label="Incoming call" name={name} />
    <RoundAction label="Accept call" tone="accept" onClick={onAccept}>
      <UserIcon icon={'andrsrxn:phone'} fontSize={18} />
    </RoundAction>
    <RoundAction label="Decline call" tone="danger" onClick={onDecline}>
      <UserIcon icon={'andrsrxn:phone-down'} fontSize={18} />
    </RoundAction>
  </>
)

const ProgressContent = ({
  label,
  name,
  onEnd,
}: {
  label: string
  name: string
  onEnd: () => void
}) => (
  <>
    <Mark tone="mint">
      <CallStatusWave size={14} />
    </Mark>
    <CallerDetails label={label} name={name} />
    <RoundAction label="End call" tone="danger" onClick={onEnd}>
      <UserIcon icon={'andrsrxn:phone-down'} fontSize={18} />
    </RoundAction>
  </>
)

const ActiveContent = ({
  name,
  avatarUrl,
  duration,
  onEnd,
}: {
  name: string
  avatarUrl?: string
  duration: string
  onEnd: () => void
}) => {
  return (
    <>
      <Avatar
        src={avatarUrl}
        alt={name}
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          border: '1px solid',
          borderColor: 'var(--app-avatar-border, rgba(36, 50, 61, 0.1))',
          color: 'var(--app-avatar-color, #32423E)',
          bgcolor: 'var(--app-avatar-bg, rgba(235, 241, 238, 0.94))',
        }}
      >
        <PhoneOutlined sx={{ fontSize: 18 }} />
      </Avatar>
      <CallerDetails label="Connected" name={name} />
      <Typography
        component="time"
        flexShrink={0}
        fontSize={12}
        fontWeight={700}
        color="text.secondary"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {duration}
      </Typography>
      <RoundAction label="End call" tone="danger" onClick={onEnd}>
        <UserIcon icon={'andrsrxn:phone-down'} fontSize={18} />
      </RoundAction>
    </>
  )
}

const TerminalContent = ({
  tone,
  label,
  name,
  icon,
}: {
  tone: 'pink' | 'neutral' | 'mint'
  label: string
  name: string
  icon: React.ReactNode
}) => (
  <>
    <Mark tone={tone}>{icon}</Mark>
    <CallerDetails label={label} name={name} />
  </>
)

const FailedContent = ({
  name,
  onRetry,
}: {
  name: string
  onRetry: () => void
}) => (
  <>
    <Mark tone="pink">
      <WarningAmberOutlined sx={{ fontSize: 18 }} />
    </Mark>
    <CallerDetails label="Call failed" name={name} />
    <RoundAction label="Retry call" tone="accept" onClick={onRetry}>
      <UserIcon icon={'andrsrxn:phone'} fontSize={18} />
    </RoundAction>
  </>
)

export default CommunicationWidgetLayout
